import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../contexts/LangContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, MapPin, Phone, Search } from 'lucide-react';
import TablePagination from '../components/TablePagination';

export default function StorePage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '' });
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const loadStores = useCallback(async (page = 1) => {
    try {
      const res = await api.listStores(page, 20);
      setStores(res.data.data);
      setPagination({ total: res.data.total, page: res.data.page, limit: res.data.limit, total_pages: res.data.total_pages });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStores(); }, [loadStores]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.updateStore(editing.id, form);
        toast.success(t('success'));
      } else {
        await api.createStore(form);
        toast.success(t('success'));
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: '', address: '', phone: '' });
      loadStores(pagination.page);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    }
  };

  const handleEdit = (store) => {
    setEditing(store);
    setForm({ name: store.name, address: store.address || '', phone: store.phone || '' });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await api.deleteStore(deleteId);
      toast.success(t('success'));
      setDeleteId(null);
      loadStores(pagination.page);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    }
  };

  const filtered = stores.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.address?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="store-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>{t('stores')}</h1>
          <p className="text-sm text-[#64584F]">{pagination.total} {t('stores').toLowerCase()}</p>
        </div>
        <Button
          onClick={() => { setEditing(null); setForm({ name: '', address: '', phone: '' }); setDialogOpen(true); }}
          className="bg-[#EB7B44] hover:bg-[#D96A35] text-white font-medium"
          data-testid="add-store-btn"
        >
          <Plus size={16} className="mr-2" /> {t('add')} {t('store')}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A6A2A0]" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={`${t('search')}...`}
          className="pl-9 bg-[#F6F5F5] border-transparent focus:bg-white focus:border-[#EB7B44]"
          data-testid="store-search-input"
        />
      </div>

      {/* Table */}
      <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F6F5F5] border-b border-[#CACACC]">
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('name')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden md:table-cell">{t('address')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden sm:table-cell">{t('phone')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider text-right">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-[#A6A2A0]">{t('loading')}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-[#A6A2A0]">{t('no_data')}</TableCell></TableRow>
            ) : filtered.map(store => (
              <TableRow key={store.id} className="border-b border-[#DFDEDE] hover:bg-[#F6F5F5]/50 transition-colors" data-testid={`store-row-${store.id}`}>
                <TableCell className="font-medium text-[#202020]">
                  <button
                    onClick={() => navigate(`/app/stores/${store.id}/employees`)}
                    className="hover:text-[#EB7B44] transition-colors text-left group"
                    data-testid={`store-name-${store.id}`}
                  >
                    {store.name}
                    <span className="text-xs text-[#A6A2A0] group-hover:text-[#EB7B44] ml-2 hidden sm:inline">
                      → {t('employees')}
                    </span>
                  </button>
                </TableCell>
                <TableCell className="text-[#64584F] hidden md:table-cell">{store.address || '-'}</TableCell>
                <TableCell className="text-[#64584F] hidden sm:table-cell">{store.phone || '-'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/app/stores/${store.id}/employees`)} data-testid={`store-employees-btn-${store.id}`}>
                      <Users size={15} className="text-[#64584F]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(store)} data-testid={`store-edit-btn-${store.id}`}>
                      <Pencil size={15} className="text-[#64584F]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(store.id)} data-testid={`store-delete-btn-${store.id}`}>
                      <Trash2 size={15} className="text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={pagination.page} totalPages={pagination.total_pages} total={pagination.total} limit={pagination.limit} onPageChange={(p) => loadStores(p)} />
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#202020]" style={{ fontFamily: 'Manrope' }}>
              {editing ? t('edit') : t('add')} {t('store')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('name')}</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="border-[#CACACC] focus:border-[#EB7B44]" data-testid="store-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('address')}</Label>
              <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="border-[#CACACC] focus:border-[#EB7B44]" data-testid="store-address-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('phone')}</Label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="border-[#CACACC] focus:border-[#EB7B44]" data-testid="store-phone-input" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#CACACC]">{t('cancel')}</Button>
              <Button type="submit" className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="store-save-btn">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#202020]">{t('confirm_delete')}</DialogTitle>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#CACACC]">{t('cancel')}</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-delete-btn">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
